import React, { useState, useEffect } from 'react';
import { FaFileAlt, FaSpinner, FaBrain, FaDownload, FaShieldAlt, FaChartLine, FaCalendarAlt, FaEdit, FaCheck, FaWallet } from 'react-icons/fa';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#FFD700', '#60A5FA', '#34D399', '#F472B6', '#A78BFA', '#FB923C'];

const ExecutiveSummary = ({ clientId }) => {
  const [loading, setLoading] = useState(true);
  const [allGoals, setAllGoals] = useState([]);
  const [rawGoalsData, setRawGoalsData] = useState({ education: [], marriage: [], retirement: null, custom: [] });
  const [aiInsights, setAiInsights] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [insuranceAnalysis, setInsuranceAnalysis] = useState(null);
  const [monthlyCommitments, setMonthlyCommitments] = useState({});
  const [editingGoal, setEditingGoal] = useState(null);

  useEffect(() => {
    fetchAllData();
  }, [clientId]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [edu, mar, ret, cust, insurance] = await Promise.all([
        axios.get(`${API}/education-goals/${clientId}`).catch(() => ({ data: [] })),
        axios.get(`${API}/marriage-goals/${clientId}`).catch(() => ({ data: [] })),
        axios.get(`${API}/retirement-goals/${clientId}`).catch(() => ({ data: null })),
        axios.get(`${API}/custom-goals/${clientId}`).catch(() => ({ data: [] })),
        axios.get(`${API}/insurance-analysis/${clientId}`).catch(() => ({ data: null }))
      ]);

      setInsuranceAnalysis(insurance.data);

      setRawGoalsData({
        education: edu.data || [],
        marriage: mar.data || [],
        retirement: ret.data,
        custom: cust.data || []
      });

      const goals = [];
      
      edu.data?.forEach(g => goals.push({
        name: `${g.child_name} - Education`,
        child_name: g.child_name,
        future_cost: g.future_goal_value,
        monthly_sip: g.required_monthly_sip,
        step_up_sip: g.step_up_starting_sip,
        life_insurance: g.life_insurance_required || g.future_goal_value,
        time_horizon: g.time_horizon_years,
        type: 'education',
        projection: g.sip_projection || []
      }));

      mar.data?.forEach(g => goals.push({
        name: `${g.child_name} - Marriage`,
        child_name: g.child_name,
        future_cost: g.future_cost,
        monthly_sip: g.required_monthly_sip,
        step_up_sip: g.step_up_starting_sip,
        life_insurance: g.life_insurance_required || g.future_cost,
        time_horizon: g.time_horizon_years,
        type: 'marriage',
        projection: g.sip_projection || []
      }));

      if (ret.data) {
        goals.push({
          name: 'Retirement',
          future_cost: ret.data.target_corpus,
          monthly_sip: ret.data.sip_needed,
          step_up_sip: ret.data.step_up_starting_sip,
          life_insurance: 0,
          time_horizon: ret.data.retirement_age - ret.data.current_age,
          type: 'retirement',
          projection: []
        });
      }

      cust.data?.forEach(g => goals.push({
        name: g.goal_name,
        future_cost: g.future_cost,
        monthly_sip: g.required_monthly_sip,
        step_up_sip: g.step_up_starting_sip,
        life_insurance: 0,
        time_horizon: g.years_to_goal,
        type: 'custom',
        projection: g.sip_projection || []
      }));

      setAllGoals(goals);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchAIInsights = async () => {
    setLoadingAI(true);
    try {
      const response = await axios.get(`${API}/executive-summary/${clientId}/ai-insights`);
      setAiInsights(response.data);
    } catch (error) {
    } finally {
      setLoadingAI(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '₹0';
    const lakhs = value / 100000;
    if (lakhs >= 100) return `₹${(lakhs / 100).toFixed(2)}Cr`;
    return `₹${lakhs.toFixed(2)}L`;
  };

  // Calculate totals
  const totalFutureCost = allGoals.reduce((sum, g) => sum + (g.future_cost || 0), 0);
  const totalMonthlySIP = allGoals.reduce((sum, g) => sum + (g.monthly_sip || 0), 0);
  const totalStepUpSIP = allGoals.reduce((sum, g) => sum + (g.step_up_sip || 0), 0);
  
  // Calculate Life Insurance Breakdown
  const protectionGap = insuranceAnalysis?.protection_gap || 0;
  const educationInsurance = allGoals.filter(g => g.type === 'education').reduce((sum, g) => sum + (g.life_insurance || 0), 0);
  const marriageInsurance = allGoals.filter(g => g.type === 'marriage').reduce((sum, g) => sum + (g.life_insurance || 0), 0);
  const totalInsurance = protectionGap + educationInsurance + marriageInsurance;

  // Prepare chart data
  const sipComparisonData = allGoals.map(g => ({
    name: g.name.length > 15 ? g.name.substring(0, 15) + '...' : g.name,
    'Regular SIP': Math.round(g.monthly_sip),
    'Step-Up SIP': Math.round(g.step_up_sip || g.monthly_sip)
  }));

  const goalDistributionData = allGoals.map((g, idx) => ({
    name: g.name,
    value: g.future_cost,
    color: COLORS[idx % COLORS.length]
  }));

  // Timeline data - combine all goals by year
  const generateTimelineData = () => {
    const maxYears = Math.max(...allGoals.map(g => g.time_horizon || 0), 1);
    const timeline = [];
    
    for (let year = 1; year <= Math.min(maxYears, 30); year++) {
      const yearData = { year };
      allGoals.forEach((goal, idx) => {
        if (year <= goal.time_horizon) {
          const monthlyRate = 0.12 / 12;
          const months = year * 12;
          const corpus = goal.monthly_sip * (((1 + monthlyRate) ** months - 1) / monthlyRate) * (1 + monthlyRate);
          yearData[goal.name] = Math.round(corpus);
        }
      });
      timeline.push(yearData);
    }
    return timeline;
  };

  const timelineData = generateTimelineData();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FaSpinner className="animate-spin text-[#FFD700]" size={32} />
      </div>
    );
  }

  if (allGoals.length === 0) {
    return (
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-12 border border-[#2D5A8F] text-center">
        <FaFileAlt className="text-[#FFD700] mx-auto mb-4" size={48} />
        <h3 className="text-2xl font-bold text-white mb-2">No Goals Added Yet</h3>
        <p className="text-gray-400">Add financial goals first to generate executive summary</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <h2 className="text-2xl font-bold text-[#FFD700] mb-2">Goal Comparison Summary</h2>
        <p className="text-gray-300">Comprehensive financial blueprint for all goals</p>
      </div>

      {/* Key Metrics - 5 Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-5 border border-[#D4AF37]">
          <p className="text-gray-400 text-xs mb-1">Total Goals</p>
          <p className="text-3xl font-bold text-[#FFD700]">{allGoals.length}</p>
        </div>

        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-5 border border-[#2D5A8F]">
          <p className="text-gray-400 text-xs mb-1">Total Wealth Required</p>
          <p className="text-2xl font-bold text-white">{formatCurrency(totalFutureCost)}</p>
        </div>

        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-5 border border-[#2D5A8F]">
          <p className="text-gray-400 text-xs mb-1">Total Monthly SIP</p>
          <p className="text-2xl font-bold text-[#FFD700]">₹{Math.round(totalMonthlySIP).toLocaleString()}</p>
        </div>

        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-5 border border-green-500">
          <p className="text-gray-400 text-xs mb-1">Step-Up SIP (Start)</p>
          <p className="text-2xl font-bold text-green-400">₹{Math.round(totalStepUpSIP).toLocaleString()}</p>
        </div>

        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-5 border border-blue-500">
          <div className="flex items-center space-x-2 mb-1">
            <FaShieldAlt className="text-blue-400" size={14} />
            <p className="text-gray-400 text-xs">Total Insurance</p>
          </div>
          <p className="text-2xl font-bold text-blue-400">{formatCurrency(totalInsurance)}</p>
        </div>
      </div>

      {/* SIP Comparison Bar Chart */}
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <h3 className="text-xl font-bold text-[#FFD700] mb-4 flex items-center space-x-2">
          <FaChartLine />
          <span>SIP Comparison by Goal</span>
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={sipComparisonData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2D5A8F" />
            <XAxis dataKey="name" stroke="#9CA3AF" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
            <YAxis stroke="#9CA3AF" tickFormatter={(val) => `₹${(val/1000).toFixed(0)}K`} />
            <Tooltip 
              contentStyle={{ backgroundColor: '#1A2942', border: '1px solid #2D5A8F', borderRadius: '8px' }}
              formatter={(value) => [`₹${Math.round(value).toLocaleString()}`, '']}
            />
            <Legend />
            <Bar dataKey="Regular SIP" fill="#FFD700" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Step-Up SIP" fill="#34D399" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
        <p className="text-gray-400 text-sm text-center mt-2">Green bars show lower starting SIP with annual step-up</p>
      </div>

      {/* Two Column - Pie Chart & Goals Table */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Goal Distribution Pie Chart */}
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
          <h3 className="text-xl font-bold text-[#FFD700] mb-4">Goal Distribution</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={goalDistributionData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
                dataKey="value"
                label={({ name, percent }) => `${name.split(' ')[0]} ${(percent * 100).toFixed(0)}%`}
                labelLine={false}
              >
                {goalDistributionData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ backgroundColor: '#1A2942', border: '1px solid #2D5A8F', borderRadius: '8px' }}
                formatter={(value) => [formatCurrency(value), 'Target']}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Goals Breakdown Table */}
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
          <h3 className="text-xl font-bold text-[#FFD700] mb-4">Goals Breakdown</h3>
          <div className="overflow-y-auto max-h-[280px]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#1A2942]">
                <tr className="border-b border-[#2D5A8F]">
                  <th className="text-left py-2 px-2 text-gray-400">Goal</th>
                  <th className="text-right py-2 px-2 text-gray-400">Target</th>
                  <th className="text-right py-2 px-2 text-gray-400">SIP</th>
                  <th className="text-right py-2 px-2 text-gray-400">Years</th>
                </tr>
              </thead>
              <tbody>
                {allGoals.map((goal, idx) => (
                  <tr key={idx} className="border-b border-[#2D5A8F]/50 hover:bg-[#1E3A5F]">
                    <td className="py-2 px-2 text-white flex items-center space-x-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }}></div>
                      <span className="truncate max-w-[120px]">{goal.name}</span>
                    </td>
                    <td className="py-2 px-2 text-right text-white">{formatCurrency(goal.future_cost)}</td>
                    <td className="py-2 px-2 text-right text-[#FFD700] font-medium">₹{Math.round(goal.monthly_sip).toLocaleString()}</td>
                    <td className="py-2 px-2 text-right text-gray-300">{goal.time_horizon}y</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-[#FFD700]">
                <tr className="font-bold">
                  <td className="py-2 px-2 text-[#FFD700]">TOTAL</td>
                  <td className="py-2 px-2 text-right text-[#FFD700]">{formatCurrency(totalFutureCost)}</td>
                  <td className="py-2 px-2 text-right text-[#FFD700]">₹{Math.round(totalMonthlySIP).toLocaleString()}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      {/* Monthly Commitment Planner */}
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#D4AF37]">
        <h3 className="text-xl font-bold text-[#FFD700] mb-4 flex items-center space-x-2">
          <FaWallet />
          <span>Monthly Commitment Planner</span>
        </h3>
        <p className="text-gray-400 text-sm mb-4">
          Customize your monthly SIP commitment for each goal. Click on the SIP amount to edit.
        </p>
        
        <div className="space-y-3">
          {allGoals.map((goal, idx) => {
            const customCommitment = monthlyCommitments[goal.name];
            const isEditing = editingGoal === goal.name;
            const currentSIP = customCommitment !== undefined ? customCommitment : Math.round(goal.monthly_sip);
            const recommended = Math.round(goal.monthly_sip);
            const diff = currentSIP - recommended;
            
            return (
              <div key={idx} className="bg-[#0A1929] rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }}></div>
                  <div>
                    <p className="text-white font-medium">{goal.name}</p>
                    <p className="text-gray-500 text-xs">
                      Recommended: ₹{recommended.toLocaleString()}/m | {goal.time_horizon} years
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  {isEditing ? (
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-400">₹</span>
                      <input
                        type="number"
                        className="w-28 px-3 py-2 bg-[#1E3A5F] border border-[#FFD700] rounded-lg text-white text-right"
                        value={currentSIP}
                        onChange={(e) => setMonthlyCommitments({
                          ...monthlyCommitments,
                          [goal.name]: parseInt(e.target.value) || 0
                        })}
                        autoFocus
                      />
                      <button
                        onClick={() => setEditingGoal(null)}
                        className="p-2 bg-green-500 rounded-lg text-white hover:bg-green-600"
                      >
                        <FaCheck size={12} />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setEditingGoal(goal.name)}
                      className="flex items-center space-x-2 px-4 py-2 bg-[#1E3A5F] rounded-lg hover:bg-[#2D5A8F] border border-[#2D5A8F] group"
                    >
                      <span className={`text-lg font-bold ${diff < 0 ? 'text-yellow-400' : diff > 0 ? 'text-green-400' : 'text-[#FFD700]'}`}>
                        ₹{currentSIP.toLocaleString()}
                      </span>
                      <span className="text-gray-400">/m</span>
                      <FaEdit size={12} className="text-gray-500 group-hover:text-[#FFD700]" />
                    </button>
                  )}
                  
                  {diff !== 0 && !isEditing && (
                    <span className={`text-xs px-2 py-1 rounded ${diff > 0 ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                      {diff > 0 ? '+' : ''}{diff.toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Total Monthly Commitment */}
        <div className="mt-4 p-4 bg-gradient-to-r from-[#D4AF37]/20 to-[#FFD700]/10 rounded-lg border border-[#FFD700]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#FFD700] font-semibold text-lg">Total Monthly Commitment</p>
              <p className="text-gray-400 text-sm">
                {Object.keys(monthlyCommitments).length > 0 
                  ? `Custom allocation (${Object.keys(monthlyCommitments).length} goals adjusted)` 
                  : 'Based on recommended SIPs'}
              </p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-[#FFD700]">
                ₹{allGoals.reduce((sum, g) => {
                  const custom = monthlyCommitments[g.name];
                  return sum + (custom !== undefined ? custom : Math.round(g.monthly_sip));
                }, 0).toLocaleString()}
              </p>
              <p className="text-gray-400 text-xs">per month</p>
            </div>
          </div>
          
          {/* Show difference from recommended */}
          {Object.keys(monthlyCommitments).length > 0 && (
            <div className="mt-3 pt-3 border-t border-[#FFD700]/30 flex justify-between text-sm">
              <span className="text-gray-400">Recommended Total: ₹{Math.round(totalMonthlySIP).toLocaleString()}/m</span>
              <span className={`font-medium ${
                allGoals.reduce((sum, g) => {
                  const custom = monthlyCommitments[g.name];
                  return sum + (custom !== undefined ? custom : Math.round(g.monthly_sip));
                }, 0) >= totalMonthlySIP ? 'text-green-400' : 'text-yellow-400'
              }`}>
                Difference: {(() => {
                  const customTotal = allGoals.reduce((sum, g) => {
                    const custom = monthlyCommitments[g.name];
                    return sum + (custom !== undefined ? custom : Math.round(g.monthly_sip));
                  }, 0);
                  const diff = customTotal - totalMonthlySIP;
                  return `${diff >= 0 ? '+' : ''}₹${Math.round(diff).toLocaleString()}`;
                })()}
              </span>
            </div>
          )}
        </div>

        {/* Warning if under-allocated */}
        {Object.keys(monthlyCommitments).length > 0 && 
         allGoals.reduce((sum, g) => {
           const custom = monthlyCommitments[g.name];
           return sum + (custom !== undefined ? custom : Math.round(g.monthly_sip));
         }, 0) < totalMonthlySIP && (
          <div className="mt-3 p-3 bg-yellow-500/10 border border-yellow-500 rounded-lg flex items-start space-x-2">
            <span className="text-yellow-400 text-xl">⚠️</span>
            <p className="text-yellow-400 text-sm">
              Your current allocation is below the recommended amount. This may delay achieving some goals or result in a shortfall.
            </p>
          </div>
        )}
      </div>

      {/* Combined Timeline Chart */}
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <h3 className="text-xl font-bold text-[#FFD700] mb-4 flex items-center space-x-2">
          <FaCalendarAlt />
          <span>Combined Investment Timeline</span>
        </h3>
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={timelineData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2D5A8F" />
            <XAxis dataKey="year" stroke="#9CA3AF" label={{ value: 'Year', position: 'insideBottom', offset: -5, fill: '#9CA3AF' }} />
            <YAxis stroke="#9CA3AF" tickFormatter={(val) => `₹${(val/100000).toFixed(0)}L`} />
            <Tooltip 
              contentStyle={{ backgroundColor: '#1A2942', border: '1px solid #2D5A8F', borderRadius: '8px' }}
              labelStyle={{ color: '#FFD700' }}
              formatter={(value) => [formatCurrency(value), '']}
            />
            <Legend />
            {allGoals.map((goal, idx) => (
              <Line 
                key={goal.name}
                type="monotone" 
                dataKey={goal.name} 
                stroke={COLORS[idx % COLORS.length]} 
                strokeWidth={2} 
                dot={false}
                name={goal.name.length > 20 ? goal.name.substring(0, 20) + '...' : goal.name}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
        <p className="text-gray-400 text-sm text-center mt-2">Projected corpus growth for each goal over time</p>
      </div>

      {/* Insurance Summary */}
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-blue-500">
        <h3 className="text-xl font-bold text-blue-400 mb-4 flex items-center space-x-2">
          <FaShieldAlt />
          <span>Life Insurance Protection Summary</span>
        </h3>
        
        {/* Protection Gap from Insurance Analysis */}
        <div className="mb-6">
          <h4 className="text-white font-semibold mb-3 border-b border-blue-500/30 pb-2">1. Income Protection (HLV Gap Analysis)</h4>
          <div className="bg-[#0A1929] rounded-lg p-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-gray-400 text-sm">Protection Gap</p>
                <p className="text-xs text-gray-500">Required Cover - Existing Cover</p>
              </div>
              <p className="text-2xl font-bold text-blue-400">{formatCurrency(protectionGap)}</p>
            </div>
            {!insuranceAnalysis && (
              <p className="text-yellow-400 text-xs mt-2">Complete Insurance Analysis to calculate protection gap</p>
            )}
          </div>
        </div>

        {/* Children's Education Goals */}
        <div className="mb-6">
          <h4 className="text-white font-semibold mb-3 border-b border-blue-500/30 pb-2">2. Children's Education Goals</h4>
          {allGoals.filter(g => g.type === 'education').length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {allGoals.filter(g => g.type === 'education').map((goal, idx) => (
                <div key={idx} className="bg-[#0A1929] rounded-lg p-4">
                  <p className="text-gray-400 text-sm">{goal.child_name || goal.name}</p>
                  <p className="text-xl font-bold text-blue-400">{formatCurrency(goal.life_insurance)}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-sm bg-[#0A1929] rounded-lg p-4">No education goals added</p>
          )}
          <div className="mt-2 text-right">
            <span className="text-gray-400 text-sm">Subtotal: </span>
            <span className="text-blue-400 font-bold">{formatCurrency(educationInsurance)}</span>
          </div>
        </div>

        {/* Children's Marriage Goals (Optional) */}
        <div className="mb-6">
          <h4 className="text-white font-semibold mb-3 border-b border-blue-500/30 pb-2">
            3. Children's Marriage Goals <span className="text-gray-400 text-sm font-normal">(Optional)</span>
          </h4>
          {allGoals.filter(g => g.type === 'marriage').length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {allGoals.filter(g => g.type === 'marriage').map((goal, idx) => (
                <div key={idx} className="bg-[#0A1929] rounded-lg p-4">
                  <p className="text-gray-400 text-sm">{goal.child_name || goal.name}</p>
                  <p className="text-xl font-bold text-blue-400">{formatCurrency(goal.life_insurance)}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-sm bg-[#0A1929] rounded-lg p-4">No marriage goals added</p>
          )}
          <div className="mt-2 text-right">
            <span className="text-gray-400 text-sm">Subtotal: </span>
            <span className="text-blue-400 font-bold">{formatCurrency(marriageInsurance)}</span>
          </div>
        </div>

        {/* Total Insurance Summary */}
        <div className="bg-gradient-to-r from-blue-900/50 to-blue-800/50 rounded-lg p-5 border border-blue-500">
          <div className="grid grid-cols-4 gap-4 text-center mb-4">
            <div>
              <p className="text-gray-400 text-xs">Protection Gap</p>
              <p className="text-lg font-bold text-white">{formatCurrency(protectionGap)}</p>
            </div>
            <div>
              <p className="text-gray-400 text-xs">+ Education</p>
              <p className="text-lg font-bold text-white">{formatCurrency(educationInsurance)}</p>
            </div>
            <div>
              <p className="text-gray-400 text-xs">+ Marriage</p>
              <p className="text-lg font-bold text-white">{formatCurrency(marriageInsurance)}</p>
            </div>
            <div className="border-l-2 border-blue-500 pl-4">
              <p className="text-gray-400 text-xs">TOTAL REQUIRED</p>
              <p className="text-2xl font-bold text-blue-400">{formatCurrency(totalInsurance)}</p>
            </div>
          </div>
          <p className="text-gray-400 text-sm text-center">
            This total life insurance ensures all financial goals are protected if the primary earner is no longer around.
          </p>
        </div>
      </div>

      {/* AI Insights */}
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#D4AF37]">
        <div className="flex items-center space-x-3 mb-4">
          <FaBrain className="text-[#FFD700]" size={24} />
          <h3 className="text-xl font-bold text-[#FFD700]">AI-Powered Goal Prioritization</h3>
        </div>

        {loadingAI ? (
          <div className="flex items-center justify-center py-8">
            <FaSpinner className="animate-spin text-[#FFD700]" size={32} />
          </div>
        ) : aiInsights ? (
          <div className="space-y-4">
            <div>
              <h4 className="text-white font-semibold mb-2">Priority Ranking</h4>
              <div className="space-y-2">
                {aiInsights.priority_ranking?.map((item, idx) => (
                  <div key={idx} className="bg-[#0A1929] rounded-lg p-3">
                    <p className="text-white font-medium">
                      <span className="text-[#FFD700]">#{item.priority}</span> - {item.goal}
                    </p>
                    <p className="text-gray-400 text-sm mt-1">{item.reason}</p>
                  </div>
                ))}
              </div>
            </div>

            {aiInsights.allocation_strategy && (
              <div>
                <h4 className="text-white font-semibold mb-2">Allocation Strategy</h4>
                <p className="text-gray-300">{aiInsights.allocation_strategy}</p>
              </div>
            )}

            {aiInsights.recommendations?.length > 0 && (
              <div>
                <h4 className="text-white font-semibold mb-2">Recommendations</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-300">
                  {aiInsights.recommendations.map((rec, idx) => (
                    <li key={idx}>{rec}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={fetchAIInsights}
            className="w-full py-3 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] transition-all"
          >
            Get AI Goal Prioritization
          </button>
        )}
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button
          onClick={() => window.print()}
          className="py-4 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] transition-all flex items-center justify-center space-x-2"
        >
          <FaFileAlt />
          <span>Print Summary</span>
        </button>

        <button
          onClick={() => alert('PDF generation will be available soon!')}
          className="py-4 bg-[#1E3A5F] text-white font-bold rounded-lg hover:bg-[#2D5A8F] transition-all flex items-center justify-center space-x-2"
        >
          <FaDownload />
          <span>Download PDF Report</span>
        </button>
      </div>

      {/* Action Plan */}
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <h3 className="text-xl font-bold text-[#FFD700] mb-4">Action Plan</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-300">
          <div className="space-y-2">
            <p className="flex items-center space-x-2"><span className="text-green-400">✓</span><span>Review all goals with the client</span></p>
            <p className="flex items-center space-x-2"><span className="text-green-400">✓</span><span>Prioritize based on urgency</span></p>
            <p className="flex items-center space-x-2"><span className="text-green-400">✓</span><span>Start with protection (Insurance)</span></p>
          </div>
          <div className="space-y-2">
            <p className="flex items-center space-x-2"><span className="text-green-400">✓</span><span>Set up SIPs for each goal</span></p>
            <p className="flex items-center space-x-2"><span className="text-green-400">✓</span><span>Quarterly review meetings</span></p>
            <p className="flex items-center space-x-2"><span className="text-green-400">✓</span><span>Annual portfolio rebalance</span></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExecutiveSummary;
